import React, { useState, useRef } from 'react';
import { Upload, File, X, Check, AlertCircle, Copy, CheckCheck } from 'lucide-react';
import { uploadSingle } from '../../constant/Constant';
import { getAcessToken, getElevateUser, getSelectedUnit } from '../../storage/Storage';
import { convertFileUrlToPublic } from '../../utils/utils';

function SingleFileUpload({ onUploadSuccess }) {
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const [copiedFileId, setCopiedFileId] = useState(null);
    const fileInputRef = useRef(null);
    const user = getElevateUser()


    console.log("user", user);



    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        handleFiles(files);
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        handleFiles(files);
    };

    const handleFiles = async (files) => {
        setIsUploading(true);
        setError(null);

        try {
            const uploadPromises = files.map(file => uploadSingleFile(file));
            const uploadResults = await Promise.all(uploadPromises);

            const newFiles = uploadResults.map((result, index) => ({
                id: Date.now() + Math.random(),
                name: files[index].name,
                size: formatFileSize(files[index].size),
                type: files[index].type,
                status: result.success ? 'uploaded' : 'failed',
                response: result.data,
                error: result.error,
                url: result.success ? convertFileUrlToPublic(result.data?.data?.url) : null,
            }));

            setUploadedFiles(prev => [...prev, ...newFiles]);

            // Call the callback function to refresh the parent component's data
            const hasSuccessfulUploads = newFiles.some(file => file.status === 'uploaded');
            if (hasSuccessfulUploads && onUploadSuccess) {
                onUploadSuccess();
            }

        } catch (error) {
            setError('Failed to upload files. Please try again.');
            console.error('Upload error:', error);
        } finally {
            setIsUploading(false);
        }
    };

    const uploadSingleFile = async (file) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('title', file.name.split('.')[0]);
            formData.append('description', `Uploaded file: ${file.name}`);
            formData.append('category', getFileCategory(file.type));
            formData.append('tags', 'upload,user-file');
            formData.append('userId', user?._id);
            formData.append('fileType', getFileType(file.type));
            formData.append('folder', "general");
            formData.append('unitIds', getSelectedUnit()?._id);



            const token = getAcessToken()

            console.log("token", token);


            const response = await fetch(uploadSingle, {

                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return { success: true, data: data };
        } catch (error) {
            console.error('File upload error:', error);
            return { success: false, error: error.message };
        }
    };

    const copyToClipboard = async (url, fileId) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopiedFileId(fileId);
            setTimeout(() => setCopiedFileId(null), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
            const textArea = document.createElement('textarea');
            textArea.value = url;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
                setCopiedFileId(fileId);
                setTimeout(() => setCopiedFileId(null), 2000);
            } catch (fallbackErr) {
                console.error('Fallback copy failed: ', fallbackErr);
            }
            document.body.removeChild(textArea);
        }
    };

    const getFileCategory = (mimeType) => {
        if (mimeType.startsWith('image/')) return 'image';
        if (mimeType.startsWith('video/')) return 'video';
        if (mimeType.startsWith('audio/')) return 'audio';
        if (mimeType.includes('pdf') || mimeType.includes('document')) return 'document';
        return 'other';
    };

    const getFileType = (mimeType) => {
        if (mimeType.startsWith('image/')) return 'image';
        if (mimeType.startsWith('video/')) return 'video';
        if (mimeType.startsWith('audio/')) return 'audio';
        return 'document';
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const removeFile = (fileId) => {
        setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
    };

    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    const retryFailedUploads = () => {
        const failedFiles = uploadedFiles.filter(file => file.status === 'failed');
        if (failedFiles.length > 0) {
            setUploadedFiles(prev => prev.filter(file => file.status !== 'failed'));
        }
    };

    return (
        <div>
            <div className="p-6 max-w-4xl mx-auto">
                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                        <AlertCircle className="w-5 h-5 text-red-500" />
                        <span className="text-red-700">{error}</span>
                    </div>
                )}

                <div
                    className={`
                        relative border-2 border-dashed rounded-lg p-12 text-center transition-all duration-300
                        ${isDragOver
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
                        }
                        ${isUploading ? 'pointer-events-none opacity-70' : 'cursor-pointer'}
                    `}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={openFileDialog}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                        accept="image/*,application/pdf,video/*"
                    />


                    <div className="flex flex-col items-center space-y-4">
                        <div className={`
                            p-4 rounded-full transition-colors duration-300
                            ${isDragOver ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}
                        `}>
                            <Upload className="w-8 h-8" />
                        </div>

                        <div>
                            <p className="text-lg text-gray-600 mb-2">
                                <span className="font-medium">Drag & Drop your files</span> or{' '}
                                <span className="text-blue-500 font-medium underline cursor-pointer">
                                    Browse
                                </span>
                            </p>
                            <p className="text-sm text-gray-400">
                                Supported: Images (.jpg, .png), Videos (.mp4), PDFs (.pdf)
                            </p>
                        </div>
                    </div>

                    {isUploading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 rounded-lg">
                            <div className="flex items-center space-x-2">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                                <span className="text-gray-600">Uploading...</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Uploaded Files List - Keep this for immediate feedback */}
                {uploadedFiles.length > 0 && (
                    <div className="mt-8">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">
                            Recent Uploads ({uploadedFiles.length})
                        </h3>

                        <div className="space-y-3">
                            {uploadedFiles.map((file) => (
                                <div
                                    key={file.id}
                                    className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className={`p-2 rounded-full ${file.status === 'uploaded' ? 'bg-green-100' : 'bg-red-100'
                                            }`}>
                                            <File className={`w-5 h-5 ${file.status === 'uploaded' ? 'text-green-600' : 'text-red-600'
                                                }`} />
                                        </div>

                                        <div className="flex-1">
                                            <p className="font-medium text-gray-800">{file.name}</p>
                                            <p className="text-sm text-gray-500">{file.size}</p>
                                            {file.error && (
                                                <p className="text-sm text-red-500">{file.error}</p>
                                            )}
                                            {file.url && (
                                                <p className="text-xs text-blue-600 truncate max-w-md mt-1">
                                                    {file.url}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <div className={`flex items-center space-x-1 ${file.status === 'uploaded' ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                            {file.status === 'uploaded' ? (
                                                <Check className="w-4 h-4" />
                                            ) : (
                                                <AlertCircle className="w-4 h-4" />
                                            )}
                                            <span className="text-sm font-medium">
                                                {file.status === 'uploaded' ? 'Uploaded' : 'Failed'}
                                            </span>
                                        </div>

                                        {file.status === 'uploaded' && file.url && (
                                            <button
                                                onClick={() => copyToClipboard(file.url, file.id)}
                                                className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm font-medium transition-all ${copiedFileId === file.id
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                                    }`}
                                                title="Copy file URL"
                                            >
                                                {copiedFileId === file.id ? (
                                                    <>
                                                        <CheckCheck className="w-4 h-4" />
                                                        <span>Copied!</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy className="w-4 h-4" />
                                                        <span>Copy Link</span>
                                                    </>
                                                )}
                                            </button>
                                        )}

                                        <button
                                            onClick={() => removeFile(file.id)}
                                            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                        >
                                            <X className="w-4 h-4 text-gray-400 hover:text-red-500" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 flex space-x-3">
                            {uploadedFiles.some(file => file.status === 'failed') && (
                                <button
                                    onClick={retryFailedUploads}
                                    className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                                >
                                    Retry Failed
                                </button>
                            )}
                            <button
                                onClick={() => setUploadedFiles([])}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SingleFileUpload;